import { parse as parseYaml } from "yaml";
import { z } from "zod";
import { primaryNatures, type PrimaryNature } from "./guide";

export const fileOrderModes = ["structural-story", "review-priority", "author-order", "alphabetical"] as const;
export type FileOrderMode = (typeof fileOrderModes)[number];

export const defaultCollapsedFilePatterns = [
  "**/*.Designer.cs",
  "**/*ModelSnapshot.cs",
  "**/*.fixture.*",
] as const;

const idSchema = z.string().regex(/^[a-z0-9][a-z0-9-]*$/);
const pathPatternSchema = z.string().min(1).refine(
  (value) => !value.startsWith("/") && !value.includes("\\") && !value.split("/").includes(".."),
  "must be a safe repository-relative glob",
);

const matcherSchema = z.object({
  id: idSchema,
  nature: z.enum(primaryNatures),
  paths: z.array(pathPatternSchema).min(1),
});

export const chewConfigSchema = z.object({
  schemaVersion: z.literal(1),
  product: z.literal("CHEW"),
  queue: z.object({
    defaultLens: z.enum(["anything-useful", "quick-wins"]),
    ageFloorDays: z.number().int().nonnegative(),
    scheduleDrafts: z.boolean().default(false),
  }),
  review: z.object({
    guideDirectory: z.string().min(1),
    assignmentUnit: z.literal("pull-request"),
    hardLocks: z.boolean(),
    defaultFileOrder: z.enum(fileOrderModes).default("structural-story"),
    collapseByDefault: z.array(pathPatternSchema).default([...defaultCollapsedFilePatterns]),
  }),
  classification: z.object({
    matchers: z.array(matcherSchema).default([]),
  }).default({ matchers: [] }),
  effectiveDiff: z.object({
    excludeNatures: z.array(z.enum(primaryNatures)).default(["tests-proof", "generated"]),
  }).default({ excludeNatures: ["tests-proof", "generated"] }),
  gates: z.object({
    chewfile: z.object({
      enforce: z.boolean().default(false),
      requiredEffectiveChangedLoc: z.number().int().positive().default(500),
      requireForStackedPulls: z.boolean().default(true),
      requiredPaths: z.array(pathPatternSchema).default([]),
      requiredLabels: z.array(z.string().min(1)).default(["chew/required"]),
    }).default({
      enforce: false,
      requiredEffectiveChangedLoc: 500,
      requireForStackedPulls: true,
      requiredPaths: [],
      requiredLabels: ["chew/required"],
    }),
  }).default({
    chewfile: {
      enforce: false,
      requiredEffectiveChangedLoc: 500,
      requireForStackedPulls: true,
      requiredPaths: [],
      requiredLabels: ["chew/required"],
    },
  }),
});

export type ChewConfig = z.infer<typeof chewConfigSchema>;

export const defaultChewConfig: ChewConfig = chewConfigSchema.parse({
  schemaVersion: 1,
  product: "CHEW",
  queue: { defaultLens: "anything-useful", ageFloorDays: 7, scheduleDrafts: false },
  review: {
    guideDirectory: "pulls",
    assignmentUnit: "pull-request",
    hardLocks: false,
    defaultFileOrder: "structural-story",
    collapseByDefault: [...defaultCollapsedFilePatterns],
  },
  classification: {
    matchers: [
      {
        id: "generated",
        nature: "generated",
        paths: [
          "**/*.generated.*",
          "**/*.Designer.cs",
          "**/*ModelSnapshot.cs",
          "**/Goldens/**",
          "**/dist/**",
        ],
      },
      {
        id: "tests",
        nature: "tests-proof",
        paths: [
          "**/*Tests/**",
          "**/*Test/**",
          "**/*Tests.cs",
          "**/*Test.cs",
          "**/__tests__/**",
          "**/*.test.*",
          "**/*.spec.*",
          "**/*.fixture.*",
        ],
      },
    ],
  },
});

function globExpression(pattern: string): RegExp {
  let expression = "^";
  for (let index = 0; index < pattern.length; index += 1) {
    const character = pattern[index]!;
    if (character === "*" && pattern[index + 1] === "*") {
      if (pattern[index + 2] === "/") {
        expression += "(?:.*/)?";
        index += 2;
      } else {
        expression += ".*";
        index += 1;
      }
    } else if (character === "*") expression += "[^/]*";
    else if (character === "?") expression += "[^/]";
    else expression += character.replace(/[|\\{}()[\]^$+?.]/g, "\\$&");
  }
  return new RegExp(`${expression}$`, "i");
}

export function pathMatches(pattern: string, path: string): boolean {
  return globExpression(pattern).test(path);
}

/** Ordered matchers provide authoring defaults. The first match wins. */
export function inferredPathNature(path: string, config: ChewConfig): PrimaryNature | undefined {
  return config.classification.matchers.find((matcher) =>
    matcher.paths.some((pattern) => pathMatches(pattern, path)),
  )?.nature;
}

export function pathRequiresChewfile(path: string, config: ChewConfig): boolean {
  return config.gates.chewfile.requiredPaths.some((pattern) => pathMatches(pattern, path));
}

export function parseChewConfigYaml(source: string): ChewConfig {
  return chewConfigSchema.parse(parseYaml(source));
}
